import { t } from 'i18next'
import TooltipMenu, { Option } from './MoreEventActionsButton'
import {
  DocType,
  EventDoc,
  EventRecurrence,
  EventStatus,
  Exclusion
} from 'types/api/changes'
import { Model } from 'types/model'
import { IEventDetails, useEventDetails } from 'hooks/useEventDetails'
import { addTempEventToDate, localEventById } from 'services/db/Queries/Event'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { is24meApp } from 'utils/appType'
import { Tier } from 'utils/api/accounts'
import { instanceStatus } from 'components/calendar/components/Event'
import { updateEventOnServer } from 'services/api/event'
import { useCalendar } from 'components/providers/CalendarProvider'
import dayjs from 'dayjs'
import { getDefaultTimezone } from 'index'
import {
  ONBOARDING_MODE,
  SingleTutor,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import { useTour } from '@reactour/tour'
import { useEventPopover } from 'components/calendar/eventPopover/EventPopoverProvider'
import { EventShareUtils } from 'utils/share'
import Logger from 'services/Logger'
import { isTrulyEmpty } from 'utils/object'
import Database from 'services/db/Database'

interface EventsActionsMenuProps {
  instanceStart: number
  instanceEnd: number
  event?: Model<EventDoc> | EventDoc
  eventHook: IEventDetails
  disabled?: boolean
  onClose: () => void
  closePopup: (b?: boolean) => void
  updateStatus?: (status: EventStatus) => void
}

export default function EventsActionsMenu(props: EventsActionsMenuProps) {
  let options: Option[] = [
    { title: t('duplicate'), neededTier: is24meApp() ? Tier.PRO : undefined }
  ]

  if (
    is24meApp() &&
    (props.event?.Type === DocType.TASK || props.event?.Type === DocType.NOTE)
  ) {
    if (
      instanceStatus(props.event, props.instanceStart) === EventStatus.COMPLETED
    ) {
      options.push({ title: t('uncomplete') })
    } else {
      options.push({ title: t('archive'), color: 'red' })
    }
  }

  let type = t('event').toLowerCase()

  if (props.event?.Type === DocType.TASK) {
    type = t('task').toLowerCase()
  } else if (props.event?.Type === DocType.NOTE) {
    type = t('note').toLowerCase()
  }

  const shareTitle = t('shareDetailsOf', { type: type })
  options.push({
    title: shareTitle
  })

  const calendar = useCalendar()
  const tfmTourProvider = useTfmTourProvider()
  const tour = useTour()
  const { closePopover } = useEventPopover()

  return (
    <TooltipMenu
      disabled={props.disabled}
      options={options}
      onSelectOption={function (option: string): boolean {
        if (option === t('duplicate')) {
          LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
          LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)
          closePopover()
          props.eventHook.setSupplementaryGroupIds([])
          props.eventHook.cacheEventData()
          const dayjsInstance = dayjs.unix(props.instanceStart / 1000)
          // .add(props.event.AllDay === '1' ? 12 : 0, 'hour')

          calendar.onGoToDate(dayjsInstance.toDate())
          addTempEventToDate(
            Number(dayjsInstance.unix()),
            Number(props.instanceEnd / 1000),
            props.event?.GroupID ?? '',
            props.event?.AllDay === '1',
            props.event?.Type,
            undefined,
            props.event?.ThirdPartyID,
            'duplicated',
            props.event?.TaskType
          ).then((event) => {
            Database.write(async () => {
              event.update((local) => {
                local.Reminder = props.event?.Reminder ?? []
              })
            })

            setTimeout(() => {
              tfmTourProvider.setMode(ONBOARDING_MODE.DUPLICATED)
              if (tour.setSteps && tfmTourProvider.getStepsByMode) {
                tour.setSteps(
                  tfmTourProvider.getStepsByMode(ONBOARDING_MODE.DUPLICATED)
                )
              }
              tour.setCurrentStep(0)
              tour.setIsOpen(true)
            }, 250)
          })
        } else if (option === t('complete')) {
          if (props.updateStatus) props.updateStatus(EventStatus.COMPLETED)
          return true
        } else if (option === t('archive')) {
          if (props.updateStatus) props.updateStatus(EventStatus.DELETED)
          return true
        } else if (option === t('uncomplete')) {
          if (props.updateStatus) {
            localEventById(props.event?._id ?? '').then((event) => {
              if (event.Status === EventStatus.COMPLETED) {
                const data = {
                  Status: EventStatus.ACTIVE,
                  Recurrence:
                    event.Recurrence &&
                    typeof event.Recurrence !== 'string' &&
                    !('0' in event.Recurrence)
                      ? { ...event.Recurrence, Exclusion: [] }
                      : undefined
                }

                updateEventOnServer(event, {
                  ...data,
                  SupplementaryGroupsIDs:
                    props.event?.SupplementaryGroupsIDs ?? []
                })
              } else if (event.Status === EventStatus.ACTIVE) {
                const eventRecurrence = event.Recurrence as EventRecurrence
                updateEventOnServer(event, {
                  Status: EventStatus.ACTIVE,
                  Recurrence: {
                    ...eventRecurrence,
                    Exclusion: [
                      ...(eventRecurrence.Exclusion ?? []),
                      {
                        Date: `${
                          dayjs(props.instanceStart)
                            .add(event.AllDay === '1' ? 12 : 0, 'hour')
                            .tz('UTC')
                            .startOf('day')
                            .valueOf() / 1000
                        }`,
                        Status: EventStatus.ACTIVE,
                        ExclusionDate: dayjs(Number(props.instanceStart) * 1000)
                          .tz(getDefaultTimezone())
                          .format('YYYYMMDDTHHmmss')
                      }
                    ]
                      .reverse()
                      .filter(
                        (item: Exclusion, index: number, self: Exclusion[]) =>
                          index ===
                          self.findIndex((i: Exclusion) => i.Date === item.Date)
                      )
                      .filter(
                        (it, index, self) =>
                          index ===
                          self.findIndex((i) => {
                            const tz =
                              (props.event?.TimeZoneNameID?.length ?? 0) > 0
                                ? props.event?.TimeZoneNameID
                                : getDefaultTimezone()
                            const dayjsExclusion = dayjs(
                              Number(i.Date) * 1000
                            ).startOf('day')
                            const dayjsEvent = dayjs(
                              Number(props.instanceStart) * 1000
                            ).tz(tz)

                            return (
                              dayjsExclusion.format('YYYY-MM-DD') ===
                              dayjsEvent.format('YYYY-MM-DD')
                            )
                          })
                      )
                      .filter((exclusion) => {
                        return exclusion.Status !== EventStatus.ACTIVE
                      })
                  },
                  SupplementaryGroupsIDs: event.SupplementaryGroupsIDs ?? []
                })
              }
            })
          }
          return true
        } else if (option === t('shareDetailsOf', { type: type })) {
          const shareText = EventShareUtils.buildEventShareText({
            text: props.eventHook.getValues('Text'),
            note:
              props.eventHook.getValues('Note') !== ''
                ? props.eventHook.getValues('Note')
                : undefined,
            notes:
              (typeof props.eventHook.getValues('Notes') !== 'string' &&
                props.eventHook
                  .getValues('Notes')
                  ?.map((note) => ({ note: note.Note }))) ||
              undefined,
            startDate: props.eventHook.getValues('StartDate'),
            endDate: props.eventHook.getValues('EndDate'),
            allDay: props.eventHook.getValues('AllDay') === '1',
            location: props.eventHook.getValues('Location.Address')
          })

          if (navigator.share) {
            try {
              Logger.blue('trying to share', shareText)
              navigator
                .share({
                  text: shareText
                })
                .catch((error) => {
                  console.error('Error sharing:', error)
                })
            } catch (e) {
              console.error(e)
            }
          } else {
            console.error('Not supported')
          }
          return false
        }

        return true
      }}
    />
  )
}
